import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { AppShell } from '@/layouts/AppShell';
import { RequireAuth, RequirePermission } from '@/routes/RequireAuth';
import { LoginPage } from '@/routes/pages/LoginPage';
import { features } from '@/lib/featureFlags';
import {
  HomePage,
  PatientsPage,
  PatientRegistryPage,
  AddStockPage,
  IssuePage,
  AdjustPage,
  ActivityPage,
  LabWorklistPage,
  LabOrderDetailPage,
  NewLabOrderPage,
  LabReportsPage,
  LabConfigPage,
  PeoplePage,
  RolesPage,
  StockStatusPage,
  AuditLogPage,
  AccountPage,
  IssuingAnalyticsPage,
  AptCounterPage,
  PublicCounterPage,
  PublicRegisterPage,
  ConsultationPage,
  VisitSummaryPage,
  VisitsPage,
  RecordVisitPage,
  EditVisitPage,
  ConsultingPage,
} from '@/routes/pages';

/**
 * Everything inside one clinic's URL namespace.
 *
 * The router is mounted with `basename="/<slug>"`, so every path below is
 * written WITHOUT the slug and resolves under it — `/patients` renders at
 * `/acme/patients`. That is deliberate and load-bearing: it is what lets the
 * nav table, every `<Link>`, every `navigate()` and every `useMatch()` in the
 * app stay exactly as they were before clinics had their own URLs. Do not add
 * the slug to a path here.
 *
 * The clinic in the URL is not a second source of tenant truth — the API still
 * scopes every request by the JWT's clinic claim. `RequireAuth` reconciles the
 * two, sending a member whose URL names someone else's clinic to their own.
 */
export function TenantRoutes() {
  return (
    <Routes>
      {/* The clinic's own front door. A deep link into the clinic bounces here
          and returns to its destination after sign-in, which the global /login
          cannot do — it does not know which clinic the link was for. */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>
      {/* Public waiting-room display — no auth, no app shell. Opened via a
          printed QR; receives live updates over WebSocket. */}
      <Route path="/p/counter" element={<PublicCounterPage />} />
      {/* Public patient self-registration — no auth, no app shell. Opened via a
          printed QR; submissions queue for staff review rather than creating a
          patient directly. */}
      <Route path="/p/register" element={<PublicRegisterPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        {/* Patient Registry: search + register. The history-free counterpart to
            /patients, gated on reading patients rather than on the clinical
            record. */}
        <Route
          path="/registry"
          element={
            <RequirePermission permission="patients.read">
              <PatientRegistryPage />
            </RequirePermission>
          }
        />
        {/* Self-registration review queue. Declared before /registry/:patientId
            so the static segment wins — the page matches on route, not on a
            reserved id value. */}
        <Route
          path="/registry/pending"
          element={
            <RequirePermission permission="self_registrations.read">
              <PatientRegistryPage />
            </RequirePermission>
          }
        />
        <Route
          path="/registry/pending/:submissionId"
          element={
            <RequirePermission permission="self_registrations.read">
              <PatientRegistryPage />
            </RequirePermission>
          }
        />
        <Route
          path="/registry/:patientId"
          element={
            <RequirePermission permission="patients.read">
              <PatientRegistryPage />
            </RequirePermission>
          }
        />
        {/* Patients: the doctor's full clinical record (history + consultations),
            so the whole tree is admin-only. The page reads the param itself and
            renders master–detail vs drill-in. */}
        <Route
          path="/patients"
          element={
            <RequirePermission permission="clinical.consultations.read">
              <PatientsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/patients/:patientId"
          element={
            <RequirePermission permission="clinical.consultations.read">
              <PatientsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/patients/:patientId/consult/:consultationId"
          element={
            features.doctorConsultEntry ? (
              <RequirePermission permission="clinical.consultations.write">
                <ConsultationPage />
              </RequirePermission>
            ) : (
              <ConsultEntryDisabledRedirect />
            )
          }
        />
        <Route
          path="/patients/:patientId/visit/:consultationId"
          element={
            <RequirePermission permission="clinical.consultations.read">
              <VisitSummaryPage />
            </RequirePermission>
          }
        />
        {/* The doctor's screen during a session: the number being served and
            that patient's past visits. Reading visits is the clinical read key. */}
        <Route
          path="/consulting"
          element={
            <RequirePermission permission="clinical.consultations.read">
              <ConsultingPage />
            </RequirePermission>
          }
        />
        {/* Typing up the doctor's paper. All three are the one key the visits API
            enforces; none of them needs — or grants — the clinical read keys. */}
        <Route
          path="/visits"
          element={
            <RequirePermission permission="visits.record">
              <VisitsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/visits/new/:patientId"
          element={
            <RequirePermission permission="visits.record">
              <RecordVisitPage />
            </RequirePermission>
          }
        />
        <Route
          path="/visits/:visitId"
          element={
            <RequirePermission permission="visits.record">
              <EditVisitPage />
            </RequirePermission>
          }
        />
        {/* Each of these mirrors the key its page's API enforces, so a member
            without it is redirected rather than shown a screen that 403s on
            load or on submit. */}
        {/* Lab desk. Each route names the key its API enforces, matching the
            nav leaf — a member without it is redirected rather than shown a
            screen that 403s on load. */}
        <Route
          path="/lab"
          element={
            <RequirePermission permission="lab.read">
              <LabWorklistPage />
            </RequirePermission>
          }
        />
        {/* Static, so these outrank `/lab/:orderId` regardless of declaration
            order — but declared first so that stays obvious to a reader. */}
        <Route
          path="/lab/reports"
          element={
            <RequirePermission permission="lab.read">
              <LabReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/lab/catalogue"
          element={
            <RequirePermission permission="lab.catalogue.manage">
              <LabConfigPage />
            </RequirePermission>
          }
        />
        <Route
          path="/lab/new"
          element={
            <RequirePermission permission="lab.orders.create">
              <NewLabOrderPage />
            </RequirePermission>
          }
        />
        <Route
          path="/lab/:orderId"
          element={
            <RequirePermission permission="lab.read">
              <LabOrderDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/stock"
          element={
            <RequirePermission permission="inventory.items.read">
              <StockStatusPage />
            </RequirePermission>
          }
        />
        <Route
          path="/add"
          element={
            <RequirePermission permission="inventory.stock.in">
              <AddStockPage />
            </RequirePermission>
          }
        />
        <Route
          path="/issue"
          element={
            <RequirePermission permission="inventory.stock.issue">
              <IssuePage />
            </RequirePermission>
          }
        />
        <Route
          path="/adjust"
          element={
            <RequirePermission permission="inventory.stock.adjust">
              <AdjustPage />
            </RequirePermission>
          }
        />
        <Route
          path="/activity"
          element={
            <RequirePermission permission="inventory.movements.read">
              <ActivityPage />
            </RequirePermission>
          }
        />
        <Route
          path="/apt-counter"
          element={
            <RequirePermission permission="apt_counter.read">
              <AptCounterPage />
            </RequirePermission>
          }
        />
        <Route
          path="/people"
          element={
            <RequirePermission permission="members.read">
              <PeoplePage />
            </RequirePermission>
          }
        />
        <Route
          path="/roles"
          element={
            <RequirePermission permission="roles.read">
              <RolesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/audit-log"
          element={
            <RequirePermission permission="audit.read">
              <AuditLogPage />
            </RequirePermission>
          }
        />
        {/* Your own account. No RequirePermission: an account is not a clinic's
            to grant, so every signed-in member reaches this — the enclosing
            RequireAuth is the whole guard. */}
        <Route path="/account" element={<AccountPage />} />
        <Route
          path="/analytics/issuing"
          element={
            <RequirePermission permission="analytics.read">
              <IssuingAnalyticsPage />
            </RequirePermission>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Where `/patients/:patientId/consult/:consultationId` lands while the doctor's
 * consultation form is switched off (`features.doctorConsultEntry`).
 *
 * A redirect rather than an unregistered route: bookmarks and browser history
 * still hold these URLs, and the catch-all would drop them on the home page
 * with no hint of which patient they were about. The read-only summary of the
 * same visit is the nearest thing that still exists, and it carries its own
 * permission guard.
 */
export function ConsultEntryDisabledRedirect() {
  const { patientId, consultationId } = useParams();
  return <Navigate to={`/patients/${patientId}/visit/${consultationId}`} replace />;
}
